/**
 * ターゲット関連のテクスチャを生成するクラス
 */
export class TargetTexture {
    /**
     * 全てのターゲットテクスチャを生成する
     * @param {Phaser.Scene} scene - テクスチャを生成するシーン
     * @param {Object} options - テクスチャ生成オプション
     * @param {number} options.baseSize - ターゲットの基本サイズ（デフォルト: 128）
     * @returns {boolean} テクスチャ生成の成否
     */
    static createAll(scene, options = {}) {
        const baseSize = options.baseSize || 128;
        
        try {
            // 黄色のターゲットテクスチャを生成
            const yellowSuccess = this.createYellowTarget(scene, { baseSize });
            const siteSuccess = this.createTargetSite(scene, { baseSize });
            
            // 黄色のサイトテクスチャを生成
            const yellowSiteSuccess = this.createColoredTargetSite(scene, { baseSize, color: 0xffff00, name: 'target_site_yellow' });
            
            // 全てのテクスチャが正常に生成されたかチェック
            const allSuccess = yellowSuccess && siteSuccess && yellowSiteSuccess;
            
            if (allSuccess) {
                console.log('TargetTexture: 全てのターゲットテクスチャを生成しました');
            } else {
                console.error('TargetTexture: 一部のターゲットテクスチャの生成に失敗しました');
            }
            
            return allSuccess;
        } catch (error) {
            console.error('TargetTexture: ターゲットテクスチャの生成中にエラーが発生しました', error);
            return false;
        }
    }
    
    /**
     * 黄色のターゲットテクスチャを生成する
     * @param {Phaser.Scene} scene - テクスチャを生成するシーン
     * @param {Object} options - テクスチャ生成オプション
     * @param {number} options.baseSize - ターゲットの基本サイズ（デフォルト: 128）
     * @param {string} options.textureName - 生成するテクスチャの名前（デフォルト: 'target_yellow'）
     * @returns {boolean} テクスチャ生成の成否
     */
    static createYellowTarget(scene, options = {}) {
        const baseSize = options.baseSize || 128;
        const textureName = options.textureName || 'target_yellow';
        const halfSize = baseSize / 2;
        
        try {
            // 既にテクスチャが存在する場合は作成しない
            if (scene.textures.exists(textureName)) {
                console.log(`TargetTexture: ${textureName}テクスチャは既に存在します`);
                return true;
            }
            
            const graphics = scene.add.graphics();
            graphics.fillStyle(0xffff00, 1); // 黄色
            graphics.fillCircle(halfSize, halfSize, halfSize - 2);
            graphics.lineStyle(2, 0xffffff);
            graphics.strokeCircle(halfSize, halfSize, halfSize - 2);
            graphics.generateTexture(textureName, baseSize, baseSize);
            graphics.destroy();
            
            console.log(`TargetTexture: ${textureName}テクスチャを生成しました`);
            return true;
        } catch (error) {
            console.error(`TargetTexture: ${textureName}テクスチャの生成中にエラーが発生しました`, error);
            return this.createFallbackTarget(scene, textureName, 0xffff00);
        }
    }
    
    
    /**
     * ターゲットサイトのテクスチャを生成する
     * @param {Phaser.Scene} scene - テクスチャを生成するシーン
     * @param {Object} options - テクスチャ生成オプション
     * @param {number} options.baseSize - ターゲットの基本サイズ（デフォルト: 128）
     * @param {string} options.textureName - 生成するテクスチャの名前（デフォルト: 'target_site'）
     * @returns {boolean} テクスチャ生成の成否
     */
    static createTargetSite(scene, options = {}) {
        const baseSize = options.baseSize || 128;
        const textureName = options.textureName || 'target_site';
        const halfSize = baseSize / 2;
        
        try {
            // 既にテクスチャが存在する場合は作成しない
            if (scene.textures.exists(textureName)) {
                console.log(`TargetTexture: ${textureName}テクスチャは既に存在します`);
                return true;
            }
            
            const graphics = scene.add.graphics();
            // 外側の円
            graphics.lineStyle(2, 0xffffff);
            graphics.strokeCircle(halfSize, halfSize, halfSize * 1.2);
            // 内側の円
            graphics.lineStyle(1, 0xffffff);
            graphics.strokeCircle(halfSize, halfSize, halfSize * 0.8);
            // 十字線（横）
            graphics.lineStyle(1, 0xffffff);
            graphics.lineBetween(0, halfSize, baseSize, halfSize);
            // 十字線（縦）
            graphics.lineBetween(halfSize, 0, halfSize, baseSize);
            graphics.generateTexture(textureName, baseSize, baseSize);
            graphics.destroy();
            
            console.log(`TargetTexture: ${textureName}テクスチャを生成しました`);
            return true;
        } catch (error) {
            console.error(`TargetTexture: ${textureName}テクスチャの生成中にエラーが発生しました`, error);
            
            // フォールバックとして単純な十字線のテクスチャを生成
            try {
                const fallbackGraphics = scene.add.graphics();
                fallbackGraphics.lineStyle(2, 0xffffff);
                fallbackGraphics.strokeCircle(halfSize, halfSize, halfSize);
                fallbackGraphics.lineBetween(0, halfSize, baseSize, halfSize);
                fallbackGraphics.lineBetween(halfSize, 0, halfSize, baseSize);
                fallbackGraphics.generateTexture(textureName, baseSize, baseSize);
                fallbackGraphics.destroy();
                console.log(`TargetTexture: フォールバックの${textureName}テクスチャを生成しました`);
                return true;
            } catch (fallbackError) {
                console.error(`TargetTexture: フォールバックテクスチャの生成にも失敗しました`, fallbackError);
                return false;
            }
        }
    }
    
    /**
     * 色付きターゲットサイトのテクスチャを生成する
     * @param {Phaser.Scene} scene - テクスチャを生成するシーン
     * @param {Object} options - テクスチャ生成オプション
     * @param {number} options.baseSize - ターゲットの基本サイズ（デフォルト: 128）
     * @param {number} options.color - サイトの色
     * @param {string} options.name - テクスチャの名前
     * @returns {boolean} テクスチャ生成の成否
     */
    static createColoredTargetSite(scene, options = {}) {
        const baseSize = options.baseSize || 128;
        const color = options.color;
        const textureName = options.name;
        const halfSize = baseSize / 2;
        
        try {
            // 既にテクスチャが存在する場合は作成しない
            if (scene.textures.exists(textureName)) {
                console.log(`TargetTexture: ${textureName}テクスチャは既に存在します`);
                return true;
            }
            
            const graphics = scene.add.graphics();
            
            // 外枠（色付き）
            graphics.lineStyle(10, color, 0.9);
            graphics.strokeCircle(halfSize, halfSize, halfSize * 1.2);
            
            // 内側の円（色付き）
            graphics.lineStyle(8, color, 0.9);
            graphics.strokeCircle(halfSize, halfSize, halfSize * 0.8);
            
            // 十字線（色付き）
            graphics.lineStyle(8, color, 0.9);
            graphics.lineBetween(0, halfSize, baseSize, halfSize);
            graphics.lineBetween(halfSize, 0, halfSize, baseSize);
            
            // 中心点（色付き）
            graphics.lineStyle(2, color, 0.9);
            graphics.fillStyle(color, 0.9);
            graphics.fillCircle(halfSize, halfSize, 6);
            graphics.strokeCircle(halfSize, halfSize, 6);
            
            graphics.generateTexture(textureName, baseSize, baseSize);
            graphics.destroy();
            
            console.log(`TargetTexture: ${textureName}テクスチャを生成しました`);
            return true;
        } catch (error) {
            console.error(`TargetTexture: ${textureName}テクスチャの生成中にエラーが発生しました`, error);
            return false;
        }
    }
    
    /**
     * フォールバック用の単純なターゲットテクスチャを生成する
     * @param {Phaser.Scene} scene - テクスチャを生成するシーン
     * @param {string} textureName - 生成するテクスチャの名前
     * @param {number} color - ターゲットの色
     * @returns {boolean} テクスチャ生成の成否
     */
    static createFallbackTarget(scene, textureName, color) {
        try {
            const fallbackGraphics = scene.add.graphics();
            fallbackGraphics.fillStyle(color, 1);
            fallbackGraphics.fillCircle(64, 64, 62);
            fallbackGraphics.lineStyle(2, 0xffffff);
            fallbackGraphics.strokeCircle(64, 64, 62);
            fallbackGraphics.generateTexture(textureName, 128, 128);
            fallbackGraphics.destroy();
            console.log(`TargetTexture: フォールバックの${textureName}テクスチャを生成しました`);
            return true;
        } catch (fallbackError) {
            console.error(`TargetTexture: フォールバックテクスチャの生成にも失敗しました`, fallbackError);
            return false;
        }
    }
}
